import { useContext } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import { AuthContext } from '@/context/auth.context';

type ListInput = Parameters<
  typeof rpcClient.accountsReceivable.salesOrdersV3.list
>[0];

type ReassignInput = Parameters<
  typeof rpcClient.accountsReceivable.salesOrdersV3.reassign
>[0];

type RejectInput = Parameters<
  typeof rpcClient.accountsReceivable.salesOrdersV3.reject
>[0];

type DeleteInput = Parameters<
  typeof rpcClient.accountsReceivable.salesOrdersV3.delete
>[0];

type UnrejectInput = Parameters<
  typeof rpcClient.accountsReceivable.salesOrdersV3.unreject
>[0];

/**
 * Sales Orders v3 — paginated, org-scoped list over the OMS v3 relational draft
 * tables (`ar_draft_sales_orders`). Admin-only browse.
 */
export function useSalesOrdersV3List(input: ListInput = {}) {
  const query = useQuery(
    orpc.accountsReceivable.salesOrdersV3.list.queryOptions({ input }),
  );
  return {
    ...query,
    draftSalesOrders: query.data?.draftSalesOrders ?? [],
    pagination: query.data?.pagination,
  };
}

/**
 * On-demand fetch of every draft matching the list filters (no pagination,
 * capped server-side) for CSV export. A mutation since the Export button
 * triggers it, not state.
 */
export function useExportSalesOrdersV3List() {
  return useMutation(
    orpc.accountsReceivable.salesOrdersV3.export.mutationOptions(),
  );
}

/**
 * Sample recent orders for the current org and return the resolvable dot-paths
 * (header + line item, incl. dynamic `additionalData.*`) with example values —
 * seeds the data-path picker in the review-page config editor.
 */
export function useSampleDraftFieldPaths(limit?: number) {
  const query = useQuery(
    orpc.accountsReceivable.salesOrdersV3.sampleFieldPaths.queryOptions({
      input: limit != null ? { limit } : {},
    }),
  );
  return {
    ...query,
    header: query.data?.header ?? [],
    lineItem: query.data?.lineItem ?? [],
    sampledOrders: query.data?.sampledOrders ?? 0,
  };
}

/** Sales Orders v3 — detail (header + ordered line items + resolved UI config). */
export function useSalesOrderV3(id: string | undefined) {
  const query = useQuery({
    ...orpc.accountsReceivable.salesOrdersV3.get.queryOptions({
      input: { id: id ?? '' },
    }),
    enabled: !!id,
  });
  return {
    ...query,
    draftSalesOrder: query.data?.draftSalesOrder,
    uiConfig: query.data?.uiConfig,
  };
}

/**
 * The org's resolved Sales Order Review presentation config, standalone (no
 * draft) — powers the Quote Builder totals strip. The backend resolves from the
 * *session's active org*, not from input, so the cache key must be org-scoped
 * (same reasoning as `useOrgUiSectionConfig`).
 */
export function useSalesOrderUiConfig() {
  const { currentUser } = useContext(AuthContext);
  const options =
    orpc.accountsReceivable.salesOrdersV3.getUiConfig.queryOptions({
      input: {},
    });
  const query = useQuery({
    ...options,
    queryKey: [
      ...options.queryKey,
      { org: currentUser?.organizationId ?? 'unknown' },
    ],
  });
  return { ...query, uiConfig: query.data };
}

/**
 * Sales Orders v3 — the members of a draft's split group (the original + every
 * child a split produced). Lazy by design: pass `enabled` (e.g. only once the
 * sibling panel opens) so non-split drafts never fire the query.
 */
export function useSalesOrderSplitGroup(
  id: string | undefined,
  { enabled }: { enabled: boolean },
) {
  const query = useQuery({
    ...orpc.accountsReceivable.salesOrdersV3.getSplitGroup.queryOptions({
      input: { id: id ?? '' },
    }),
    enabled: enabled && !!id,
  });
  return { ...query, members: query.data?.members ?? [] };
}

/**
 * Sales Orders v3 — the draft's revision chain (oldest → newest), one member
 * per PO revision. Lazy like the split group: pass `enabled` so never-revised
 * drafts don't fire the query.
 */
export function useSalesOrderRevisionChain(
  id: string | undefined,
  { enabled }: { enabled: boolean },
) {
  const query = useQuery({
    ...orpc.accountsReceivable.salesOrdersV3.getRevisionChain.queryOptions({
      input: { id: id ?? '' },
    }),
    enabled: enabled && !!id,
  });
  return { ...query, members: query.data?.members ?? [] };
}

/**
 * Why a revision draft exists — trigger email, the previous revision's
 * outcome, and the attributed diff. Lazy: pass `enabled` only for revisions.
 */
export function useSalesOrderRevisionExplanation(
  id: string | undefined,
  { enabled }: { enabled: boolean },
) {
  const query = useQuery({
    ...orpc.accountsReceivable.salesOrdersV3.getRevisionExplanation.queryOptions(
      { input: { id: id ?? '' } },
    ),
    enabled: enabled && !!id,
  });
  return { ...query, explanation: query.data?.explanation ?? null };
}

/**
 * Reassign a draft sales order to another organization (OMS v3 routing).
 * On success the draft leaves the current org, so we invalidate both the
 * detail and any list queries.
 */
export function useReassignSalesOrderV3() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ReassignInput) =>
      rpcClient.accountsReceivable.salesOrdersV3.reassign(input),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.salesOrdersV3.get.key({
          input: { id: variables.id },
        }),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.salesOrdersV3.list.key(),
      });
    },
  });
}

/**
 * Reject a draft sales order (sets status REJECTED). On success the lifecycle
 * stage flips to `rejected`, so invalidate the detail + any list queries.
 */
export function useRejectSalesOrderV3() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: RejectInput) =>
      rpcClient.accountsReceivable.salesOrdersV3.reject(input),
    onSuccess: (_result, variables) =>
      invalidateDraftStatusQueries(queryClient, variables.id),
  });
}

/**
 * Admin reversal of a rejection — the order returns to the approval chain, so
 * the stepper, activity log, and list all change.
 */
export function useUnrejectSalesOrderV3() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: UnrejectInput) =>
      rpcClient.accountsReceivable.salesOrdersV3.unreject(input),
    onSuccess: (_result, variables) =>
      invalidateDraftStatusQueries(queryClient, variables.id),
  });
}

/** Detail + list + workflow state + activity — everything a status flip moves. */
function invalidateDraftStatusQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  draftSalesOrderId: string,
) {
  void queryClient.invalidateQueries({
    queryKey: orpc.accountsReceivable.salesOrdersV3.get.key({
      input: { id: draftSalesOrderId },
    }),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.accountsReceivable.salesOrdersV3.list.key(),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.accountsReceivable.draftSalesOrders.getWorkflowState.key({
      input: { draftSalesOrderId },
    }),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.accountsReceivable.draftSalesOrders.getActivity.key({
      input: { draftSalesOrderId },
    }),
  });
}

/**
 * Poll the ERP for the order number of a draft submitted via a REGISTERED
 * submission API (the EBS import response carries no number — it's assigned
 * asynchronously and retrieved via the status endpoint). On success the
 * submission record's erpOrderNumber is updated server-side, so refresh the
 * detail + list.
 */
export function usePollErpOrderNumberV3() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string }) =>
      rpcClient.accountsReceivable.salesOrdersV3.pollErpOrderNumber(input),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.salesOrdersV3.get.key({
          input: { id: variables.id },
        }),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.salesOrdersV3.list.key(),
      });
    },
  });
}

/**
 * Build the Input Submission Map payload for a draft without sending,
 * approving, or writing anything — the map tab's "Preview payload" action.
 * Pure read; nothing to invalidate.
 */
export function usePreviewErpSubmissionPayload() {
  return useMutation({
    mutationFn: async (input: {
      apiDefinitionId: string;
      draftSalesOrderId?: string;
    }) =>
      rpcClient.accountsReceivable.salesOrdersV3.previewErpSubmissionPayload(
        input,
      ),
  });
}

/**
 * Soft-delete a draft sales order. The row disappears from the org's list, so
 * invalidate the list; the caller typically navigates away from the detail.
 */
export function useDeleteSalesOrderV3() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: DeleteInput) =>
      rpcClient.accountsReceivable.salesOrdersV3.delete(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.salesOrdersV3.list.key(),
      });
    },
  });
}
